import { formatContactDisplayName } from "@domaddress/shared";
import EmailToRecipientsCopy from "./EmailToRecipientsCopy";
import type { Contact } from "@domaddress/shared";
import {
  useZendContactenContext,
  useVcardEmailSend,
  type ZendContactenContextValue,
} from "../context/ZendContactenContext";

export type { ZendContactenListItem } from "../context/ZendContactenContext";

/** Props voor modal/zijbalk (geen addToSendList nodig). */
export type ZendContactenUiBundle = Omit<ZendContactenContextValue, "addToSendList" | "items">;

/** Per lijstpagina: addToSendList met vast api-pad. */
export type VcardEmailSendBundle = ZendContactenUiBundle & {
  addToSendList: (contact: Contact) => void;
};

export { useVcardEmailSend };

export function VcardEmailToolbarButton({ b }: { b: VcardEmailSendBundle }) {
  if (!b.canZendContacten) return null;
  return (
    <button
      type="button"
      className={b.sendListOpen ? "btn-primary" : "btn-secondary"}
      onClick={b.toggleSendMode}
    >
      Zend contacten{b.sendList.length > 0 ? ` (${b.sendList.length})` : ""}
    </button>
  );
}

export function VcardEmailAside({ b }: { b: VcardEmailSendBundle }) {
  if (!b.sendListOpen || !b.canZendContacten) return null;
  return (
    <aside
      className="detail-section"
      style={{
        width: "min(100%, 300px)",
        flexShrink: 0,
        position: "sticky" as const,
        top: "0.5rem",
        alignSelf: "flex-start",
      }}
    >
      <h3 className="section-title" style={{ marginTop: 0 }}>
        Zend contacten ({b.sendList.length})
      </h3>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        Deze lijst blijft staan als u naar een andere adresboekpagina gaat. Klik per persoon op{" "}
        <strong>Toevoegen →</strong>, daarna <strong>Verder…</strong> om het Aan-veld te kopiëren of vCards te
        versturen.
      </p>
      {b.sendList.length === 0 ? (
        <p className="page-subtitle">Nog niemand toegevoegd.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 0.75rem" }}>
          {b.sendListEntries.map(({ contact: c, sendEmail }) => (
            <li
              key={c.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 8,
                padding: "0.35rem 0",
                borderBottom: "1px solid var(--border-muted, #eee)",
              }}
            >
              <span>
                {formatContactDisplayName(c)}
                {sendEmail ? (
                  <span
                    className="page-subtitle"
                    style={{ display: "block", fontSize: "0.85em", marginTop: 2 }}
                  >
                    {sendEmail}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                className="btn-icon btn-icon-danger"
                title="Uit lijst halen"
                onClick={() => b.removeFromSendList(c.id)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          type="button"
          className="btn-primary"
          style={{ width: "100%" }}
          disabled={b.sendList.length === 0}
          onClick={() => {
            b.clearSendErr();
            b.setSendModalOpen(true);
          }}
        >
          Verder…
        </button>
        {b.sendList.length > 0 && (
          <button type="button" className="btn-secondary btn-sm" style={{ width: "100%" }} onClick={b.clearSendList}>
            Lijst legen
          </button>
        )}
      </div>
    </aside>
  );
}

export function VcardEmailModal({ b }: { b: ZendContactenUiBundle }) {
  if (!b.sendModalOpen) return null;
  return (
    <div
      className="modal-overlay"
      onClick={() => !b.sendBusy && b.setSendModalOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Contacten zenden"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Zend contacten</h2>
          <button type="button" className="modal-close" onClick={() => !b.sendBusy && b.setSendModalOpen(false)}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <p className="page-subtitle">
            {b.sendList.length} geselecteerde {b.sendList.length === 1 ? "contact" : "contacten"} (eventueel uit
            meerdere lijsten).
          </p>

          <EmailToRecipientsCopy
            entries={b.sendListEntries.map(({ contact, sendEmail }) => ({ ...contact, sendEmail }))}
          />

          {b.canAdHocEmail ? (
            <>
              <hr style={{ margin: "1.25rem 0", border: "none", borderTop: "1px solid var(--surface-300, #e4dfe8)" }} />
              <h3 className="section-title" style={{ marginTop: 0 }}>
                Of: vCard per e-mail versturen
              </h3>
              <p className="page-subtitle" style={{ marginTop: 0 }}>
                Stuur .vcf-bijlagen naar één ontvanger (importeerbaar in Outlook e.d.).
              </p>
              {b.sendErr && <p className="form-error">{b.sendErr}</p>}
              <div className="form-group">
                <label>Ontvanger e-mail</label>
                <input
                  type="email"
                  className="search-input"
                  style={{ width: "100%" }}
                  value={b.sendTo}
                  onChange={(e) => b.setSendTo(e.target.value)}
                  placeholder="naam@voorbeeld.nl"
                />
              </div>
              <div className="form-group">
                <label>Tekst in de e-mail (optioneel)</label>
                <textarea
                  className="search-input"
                  style={{ width: "100%", minHeight: 100 }}
                  value={b.sendBody}
                  onChange={(e) => b.setSendBody(e.target.value)}
                  placeholder="Korte toelichting voor de ontvanger…"
                />
              </div>
              <p style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={b.sendBusy}
                  onClick={() => void b.submitSendVcards()}
                >
                  {b.sendBusy ? "Bezig…" : "Verstuur vCards"}
                </button>
              </p>
            </>
          ) : (
            <p className="page-subtitle" style={{ marginTop: "1rem" }}>
              vCard per e-mail versturen kan alleen door secretaris of beheerder. U kunt wel het Aan-veld hierboven
              kopiëren.
            </p>
          )}

          <p style={{ marginTop: "1rem" }}>
            <button
              type="button"
              className="btn-secondary"
              disabled={b.sendBusy}
              onClick={() => b.setSendModalOpen(false)}
            >
              Sluiten
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Eén modal voor de hele app (in Layout), zodat die open blijft bij navigatie. */
export function GlobalVcardEmailModal() {
  const b = useZendContactenContext();
  return <VcardEmailModal b={b} />;
}
